if (!customElements.get('predictive-search')) {
class PredictiveSearch extends SearchForm {
  static COLLECTION_MAP = {
    'bags': { title: 'Vacuum Bags', url: '/collections/bags' },
    'filters': { title: 'Vacuum Filters', url: '/collections/filters' },
    'parts': { title: 'Vacuum Parts', url: '/collections/parts' },
    'belts': { title: 'Vacuum Belts', url: '/collections/belt' },
    'miele': { title: 'Miele Vacuums', url: '/collections/miele-vacuums' },
    'sebo': { title: 'Sebo Vacuums', url: '/collections/sebo-vacuums' },
    'riccar': { title: 'Riccar Vacuums', url: '/collections/riccar-vacuums' },
    'oreck': { title: 'Oreck Vacuums', url: '/collections/oreck-vacuums' },
    'simplicity': { title: 'Simplicity Vacuums', url: '/collections/simplicity-vacuums' },
    'cleanmax': { title: 'CleanMax Vacuums', url: '/collections/cleanmax-vacuums' },
    'upright': { title: 'Upright Vacuums', url: '/collections/upright' },
    'canister': { title: 'Canister Vacuums', url: '/collections/canister' },
    'central': { title: 'Central Vacuums', url: '/collections/central-vacuums' },
    'cordless': { title: 'Cordless Vacuums', url: '/collections/cordless-vacuums' },
    'commercial': { title: 'Commercial Vacuums', url: '/collections/commercial-vacuum' },
    'robot': { title: 'Robot Vacuums', url: '/collections/robot' },
    'handheld': { title: 'Handheld Vacuums', url: '/collections/handheld-vacuum' },
    'deals': { title: 'Deals', url: '/collections/deal' },
    'attachments': { title: 'Vacuum Attachments', url: '/collections/attachments' },
    'hoses': { title: 'Vacuum Hoses', url: '/collections/hose' },
  };

  static _slashListenerRegistered = false;

  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.registerSlashShortcut();
  }

  registerSlashShortcut() {
    if (PredictiveSearch._slashListenerRegistered) return;
    PredictiveSearch._slashListenerRegistered = true;

    document.addEventListener('keydown', (event) => {
      if (event.key !== '/') return;
      const tag = document.activeElement.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || document.activeElement.isContentEditable) return;

      event.preventDefault();
      const visibleInstance = Array.from(document.querySelectorAll('predictive-search'))
        .find((el) => el.offsetParent !== null);
      if (visibleInstance) visibleInstance.input.focus();
    });
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Handle click on "Search for [term]" button (it's outside the form element)
    this.addEventListener('click', (event) => {
      const searchForButton = event.target.closest('.predictive-search__item--term');
      if (searchForButton) {
        const query = this.getQuery();
        if (query.length) {
          this.input.form.submit();
        }
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showEmptyState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && !this.querySelector('.predictive-search__empty-state')) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Check for zero results
    const hasResults = this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper');
    if (!hasResults && this.searchTerm.length) {
      this.predictiveSearchResults.innerHTML = this.buildNoResultsHTML(this.searchTerm);
    } else {
      this.highlightSearchTermInResults(this.searchTerm);
      this.injectCategoryMatch(this.searchTerm);
    }

    this.setAttribute('results', true);

    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) {
      this.setLiveRegionResults();
    } else {
      this.removeAttribute('loading');
    }

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  highlightSearchTermInResults(term) {
    if (!term) return;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');

    this.predictiveSearchResults
      .querySelectorAll('#predictive-search-results-products-list .predictive-search__item-heading')
      .forEach((el) => {
        const text = el.textContent;
        if (regex.test(text)) {
          el.innerHTML = text.replace(regex, '<mark>$1</mark>');
        }
      });
  }

  injectCategoryMatch(term) {
    if (!term) return;
    const lowerTerm = term.toLowerCase().trim();

    // Find exact match or startsWith match
    let match = null;
    for (const [key, value] of Object.entries(PredictiveSearch.COLLECTION_MAP)) {
      if (key === lowerTerm || key.startsWith(lowerTerm) || lowerTerm.startsWith(key)) {
        match = value;
        break;
      }
    }

    if (!match) return;

    const wrapper = this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) return;

    const categoryHTML = `
      <div class="predictive-search__category-match-wrapper">
        <a href="${match.url}" class="predictive-search__category-match predictive-search__item link link--text" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading h5">Browse All ${match.title}</p>
            <span class="predictive-search__category-match-arrow caption-with-letter-spacing">&rarr; View Collection</span>
          </div>
        </a>
      </div>`;

    wrapper.insertAdjacentHTML('beforebegin', categoryHTML);
  }

  buildEmptyStateHTML() {
    return `
      <div id="predictive-search-results" class="predictive-search__empty-state" role="listbox">
        <div class="predictive-search__results-groups-wrapper">
          <div class="predictive-search__result-group">
            <div>
              <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Popular Searches</h2>
              <ul class="predictive-search__results-list list-unstyled" role="group">
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-1">
                  <a href="/search?q=miele+vacuums" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Miele Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-2">
                  <a href="/search?q=sebo+vacuums" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Sebo Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-3">
                  <a href="/search?q=riccar+vacuums" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Riccar Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-4">
                  <a href="/search?q=vacuum+bags" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Vacuum Bags</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-5">
                  <a href="/search?q=vacuum+filters" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Vacuum Filters</p>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="predictive-search__result-group">
            <div>
              <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Browse Categories</h2>
              <ul class="predictive-search__results-list list-unstyled" role="group">
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-6">
                  <a href="/collections/upright" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Upright Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-7">
                  <a href="/collections/canister" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Canister Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-8">
                  <a href="/collections/central-vacuums" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Central Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-9">
                  <a href="/collections/cordless-vacuums" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Cordless Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-10">
                  <a href="/collections/commercial-vacuum" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Commercial Vacuums</p>
                    </div>
                  </a>
                </li>
                <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-empty-11">
                  <a href="/collections/deal" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5">Deals</p>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>`;
  }

  buildNoResultsHTML(searchTerm) {
    const escaped = searchTerm.replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);

    return `
      <div id="predictive-search-results" class="predictive-search__no-results" role="listbox">
        <div class="predictive-search__no-results-header">
          <p class="predictive-search__no-results-text h5">No results for &ldquo;${escaped}&rdquo;</p>
        </div>
        <div>
          <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Browse Categories</h2>
          <ul class="predictive-search__results-list list-unstyled" role="group">
            <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-noresult-1">
              <a href="/collections/miele-vacuums" class="predictive-search__item link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">Miele Vacuums</p>
                </div>
              </a>
            </li>
            <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-noresult-2">
              <a href="/collections/sebo-vacuums" class="predictive-search__item link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">Sebo Vacuums</p>
                </div>
              </a>
            </li>
            <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-noresult-3">
              <a href="/collections/riccar-vacuums" class="predictive-search__item link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">Riccar Vacuums</p>
                </div>
              </a>
            </li>
            <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-noresult-4">
              <a href="/collections/bags" class="predictive-search__item link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">Vacuum Bags</p>
                </div>
              </a>
            </li>
            <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-noresult-5">
              <a href="/collections/filters" class="predictive-search__item link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">Vacuum Filters</p>
                </div>
              </a>
            </li>
            <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-noresult-6">
              <a href="/collections/parts" class="predictive-search__item link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">Vacuum Parts</p>
                </div>
              </a>
            </li>
          </ul>
        </div>
        <div class="predictive-search__no-results-cta">
          <p>Can't find what you're looking for? Call us at <a href="tel:8606483575">(860) 648-3575</a></p>
        </div>
      </div>`;
  }

  showEmptyState() {
    this.predictiveSearchResults.innerHTML = this.buildEmptyStateHTML();
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.open();
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
} // end if (!customElements.get('predictive-search'))
